import React, { useState } from 'react';
import { Tabs } from 'antd';
import { useThrottleEffect } from 'ahooks';
import { useMemberTimeStore } from 'lowcode/utils/context/memberTimeZone';
import { EcoState, useStore, useDispatch } from '../context';
import NoteModal from '../ModalComp/NoteModal';
import Coupon from '../Coupon2';
import css from './index.module.scss';
import { useGlobal } from '@/utils/useGlobal';
import Button from '@/components/UI/Button';
import EfModal from '@/components/UI/Modal';
import { SuccessIcon, NoDataIcon } from '@/components/UI/svg/Ecocredits';

interface props {
    issuedCoupon: any[];
    openModal?: () => void;
    modal?: boolean;
    defaultSelectKey?: string;
}

interface ModuleTab {
    key: keyof EcoState;
    label: string;
}

const RewardsRedeemed: React.FC<props> = ({ issuedCoupon, modal, defaultSelectKey = '1' }) => {
    const { useLang, useRouter, isMobile } = useGlobal();
    const state = useStore();
    const dispatch = useDispatch();
    const { t } = useLang('ecocredits');
    const router = useRouter();
    const { locale } = router;
    const [currCoupon, setCurrCoupon] = useState<any>(null);
    const [currNotes, setCurrNotes] = useState<any>(null);
    const [activeKey, setActiveKey] = useState<string>(defaultSelectKey);
    const { timezoneList } = useMemberTimeStore();

    const linkMap: { [k: string]: string } = {
        '1635227277057232898': `/${locale}/ecocredits-day`,
        '1638383549539569666': 'https://us.ecoflow.com/products/outdoor-kit',
        '1638384706130059266': 'https://us.ecoflow.com/products/river-2-pro-portable-power-station',
        '1638385215599583234': 'https://us.ecoflow.com/products/delta-pro-portable-power-station',
        '1638386232449867777': 'https://ca.ecoflow.com/products/outdoor-kit',
        '1638386872819425281': 'https://ca.ecoflow.com/products/river-2-pro-portable-power-station',
        '1638387248596930562': 'https://ca.ecoflow.com/products/delta-pro-portable-power-station',
    };

    const allModules: ModuleTab[] = [
        { key: 'COUPONS_REDEEMED', label: t('discount') },
        { key: 'FREE_PRODUCTS_REDEEMED', label: t('product_text') },
        { key: 'THIRD_COUPONS_REDEEMED', label: t('gift_card') },
    ];

    const successBtnMap: any = {
        COUPONS: t('store'),
        THIRD_COUPONS: t('spend_now'),
    };

    const filterModules = allModules
        .filter((module) => state[module.key]?.length)
        ?.map((module) => ({
            ...module,
            list: state[module.key],
        }));

    const issuedModules = [
        { key: '1', label: t('discount'), list: issuedCoupon },
        { key: '2', label: t('gift_card'), list: state.ISSEUED_THIRD_COUPONS_REDEEMED },
    ]?.filter((module) => module?.list?.length);

    const shopNow = (coupon: any) => {
        const { thirdUrl, ruleType, discountId, jumpUrl } = coupon;
        if (ruleType === 'THIRD_COUPONS') {
            if (isMobile) {
                router.push(thirdUrl);
                return;
            }
            window.open(thirdUrl, '_blank');
            return;
        }

        let href: string | undefined = linkMap[discountId];
        // 读取配置的url
        if (jumpUrl) href = jumpUrl;
        if (href) {
            if (href.startsWith('http')) {
                setTimeout(() => window.open(href, '_blank'));
            } else {
                window.location.href = href;
            }
            return;
        }

        if (isMobile) {
            router.push(`https://${locale}.ecoflow.com`);
            return;
        }
        setTimeout(() => {
            window.open(`https://${locale}.ecoflow.com`, '_blank');
        });
    };

    const onClickItem = (coupon: any) => {
        if (coupon?.useStatus !== 'UNUSED') return;
        if (coupon?.ruleType === 'FREE_PRODUCTS') return;
        setCurrCoupon(coupon);
    };

    const onClickRule = (rule: any) => {
        setCurrNotes(rule);
    };

    const getShowBtn = (coupon: any) => {
        const { ruleType, thirdUrl } = coupon || {};

        if (ruleType === 'COUPONS') return true;

        if (ruleType === 'THIRD_COUPONS' && thirdUrl) return true;

        return false;
    };

    useThrottleEffect(() => {
        dispatch({
            type: 'getCouponsRedeemed',
            payload: { locale, t, timezoneList },
        });
        dispatch({
            type: 'getProducts',
            payload: { locale, timezoneList },
        });
        dispatch({
            type: 'getThiredCoupons',
            payload: { locale, timezoneList },
        });
        dispatch({
            type: 'getIssuedThiredCoupons',
            payload: { locale, timezoneList },
        });
    }, []);

    return (
        <>
            <div className={`${css.rewards_redeemed} ${modal ? 'modal' : ''}`}>
                <div className="reward-content">
                    <h5 className="credits-title">{t('rewards_redeemed')}</h5>
                    <Tabs
                        activeKey={activeKey}
                        className={`rewards-tabs ${activeKey === '1' ? 'redeemed_tab' : 'issued_tab'}`}
                        onChange={setActiveKey}
                        centered={isMobile}
                    >
                        <Tabs.TabPane tab={t('redeedmed_tab_name')} key="1">
                            {filterModules?.length ? (
                                filterModules?.map((module) => (
                                    <div key={module.key} className="module-item">
                                        <h5 className="module_title">{module.label}</h5>
                                        <ul className="rule-list">
                                            {module.list?.map((coupon: any) => (
                                                <Coupon
                                                    key={coupon?.id}
                                                    className={`rule-item ${coupon?.useStatus}`}
                                                    coupon={coupon}
                                                    disabled={coupon.useStatus !== 'UNUSED'}
                                                    hideBtn
                                                    hideAmount={['THIRD_COUPONS', 'FREE_PRODUCTS'].includes(coupon?.ruleType)}
                                                    showJumpBtn={'THIRD_COUPONS' === coupon?.ruleType && module.list?.some((coupon: any) => !!coupon.useUrl)}
                                                    onViewsRules={onClickRule}
                                                    onClickCard={onClickItem}
                                                />
                                            ))}
                                        </ul>
                                    </div>
                                ))
                            ) : (
                                <div className="no-content">
                                    <div className="no-content-icon">
                                        <NoDataIcon />
                                    </div>
                                    <p className="no-content-note">{t('no_coupon_note')}</p>
                                </div>
                            )}
                        </Tabs.TabPane>
                        <Tabs.TabPane tab={t('issued_tab_name')} key="2">
                            {!!issuedModules?.length ? (
                                issuedModules?.map((module) => (
                                    <div key={module.key} className="module-item">
                                        <h5 className="module_title">{module.label}</h5>
                                        <ul className="rule-list">
                                            {module.list?.map((coupon: any) => (
                                                <Coupon
                                                    key={coupon?.id}
                                                    className={`rule-item ${coupon?.useStatus}`}
                                                    coupon={coupon}
                                                    disabled={coupon.useStatus !== 'UNUSED'}
                                                    hideBtn
                                                    hideAmount={['THIRD_COUPONS', 'FREE_PRODUCTS'].includes(coupon?.ruleType)}
                                                    showJumpBtn={'THIRD_COUPONS' === coupon?.ruleType && module.list?.some((coupon: any) => !!coupon.useUrl)}
                                                    onViewsRules={onClickRule}
                                                    onClickCard={onClickItem}
                                                />
                                            ))}
                                        </ul>
                                    </div>
                                ))
                            ) : (
                                <div className="no-content">
                                    <div className="no-content-icon">
                                        <NoDataIcon />
                                    </div>
                                    <p className="no-content-note">{t('no_coupon_note')}</p>
                                </div>
                            )}
                        </Tabs.TabPane>
                    </Tabs>
                </div>
            </div>

            <EfModal
                className={css.efmodal}
                visible={!!currCoupon}
                zIndex={1200}
                onCancel={() => {
                    setCurrCoupon(null);
                }}
            >
                <div className="content">
                    <div className="m-icon">
                        <SuccessIcon />
                    </div>
                    <h5 className="m-title">{t('copy_desc')}</h5>

                    {getShowBtn(currCoupon) && (
                        <Button onClick={() => shopNow(currCoupon)} className="m-btn">
                            {successBtnMap[currCoupon?.ruleType] || t('store')}
                        </Button>
                    )}
                </div>
            </EfModal>
            {/* 优惠券rules弹框 */}
            <NoteModal currNotes={currNotes} setCurrNotes={setCurrNotes} disabledCoupon={currNotes?.useStatus !== 'UNUSED'} />
        </>
    );
};

export default RewardsRedeemed;
